import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApiService } from '../../../../../common/services/api.service';
import { MainEnvService } from '../../../../../common/shared/main-env.service';
import { StatisticsDateError, StatisticsDateRange, validateStatisticsRange } from './statistics-dates';
import { StatisticsDetailDialogComponent } from './statistics-detail-dialog.component';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
})
export class StatisticsComponent implements OnDestroy {

  summary: Record<string, number> = {};
  dateFrom: string;
  dateTo: string;
  /** Range the shown summary was computed with; null until the first query. */
  appliedRange: StatisticsDateRange | null = null;
  loading = false;
  private badInput = { from: false, to: false };
  private summarySubscription?: Subscription;

  get dateError(): StatisticsDateError | null {
    return validateStatisticsRange(this.dateFrom, this.dateTo, this.badInput.from || this.badInput.to);
  }

  get datesChanged(): boolean {
    return this.appliedRange != null
      && (this.appliedRange.dateFrom !== this.dateFrom || this.appliedRange.dateTo !== this.dateTo);
  }

  onDateInput(event: Event, field: 'from' | 'to'): void {
    this.badInput[field] = (event.target as HTMLInputElement).validity.badInput;
  }

  constructor(
    private http: HttpClient,
    private env: MainEnvService,
    private api: ApiService,
    public dialog: MatDialog,
    private translate: TranslateService,
  ) {
    // Default: previous month (local timezone)
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    this.dateFrom = this.formatDate(prevMonth);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    this.dateTo = this.formatDate(prevMonthEnd);
  }

  ngOnDestroy(): void {
    this.summarySubscription?.unsubscribe();
  }

  formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  loadSummary(): void {
    if (this.dateError) return;
    const range: StatisticsDateRange = { dateFrom: this.dateFrom, dateTo: this.dateTo };
    const params = new HttpParams()
      .set('filter[dateFrom]', range.dateFrom)
      .set('filter[dateTo]', range.dateTo);

    // A previous query still in flight would otherwise overwrite this one's result when it lands.
    this.summarySubscription?.unsubscribe();
    this.loading = true;
    this.summarySubscription = this.http.get<any>(`${this.env.url}/statistics/summary`, { params }).pipe(
      map(res => res.data?.attributes ?? res),
      catchError(err => this.api.handleError(err)),
    ).subscribe({
      next: res => {
        this.summary = res;
        this.appliedRange = range;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openDetail(metric: string, label: string): void {
    if (this.appliedRange == null) return;
    this.dialog.open(StatisticsDetailDialogComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      panelClass: 'statistics-detail-panel',
      data: { metric, label, dateFrom: this.appliedRange.dateFrom, dateTo: this.appliedRange.dateTo },
    });
  }

  downloadCsv(): void {
    if (this.dateError) return;
    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;
    const params = new HttpParams().set('dateFrom', dateFrom).set('dateTo', dateTo);

    this.http.get(`${this.env.url}/statistics/csv`, {
      params,
      responseType: 'blob',
    }).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistics-${dateFrom}_${dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  readonly METRIC_KEYS = ['records', 'documents', 'iaExtraction', 'invites', 'edocs'];

  get metrics(): Array<{key: string; label: string}> {
    return this.METRIC_KEYS.map(key => ({
      key,
      label: this.translate.instant(`statistics.metrics.${key}`),
    }));
  }
}
